import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FaCommentDots, FaEnvelope, FaPhone, FaPlus, FaSyncAlt } from 'react-icons/fa'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { useIsAdmin } from './useIsAdmin'
import styles from './clientCommsPanel.module.scss'

/**
 * Client-level communications history: every email/SMS sent to the claim's
 * client, in the order they were sent. Messages are shown identically
 * whether they came from the send-log import or a backfill; the
 * record_source distinction stays in the database for reporting only.
 *
 * "Record missing comm" (single-client backfill) is a Sentinel-admin tool.
 */

interface ClientCommMessage {
    id: number
    contact_id: string
    channel: 'email' | 'sms' | 'call'
    direction: string
    status: string | null
    sent_at: string | null
    subject: string | null
    from_address: string | null
    to_address: string | null
    body_text: string | null
    category: string
    template_key: string | null
    record_source: 'imported' | 'backfill'
    has_html: boolean
    s3_verified: boolean
}

interface CommTemplate {
    id: number
    template_key: string
    category: string
    channel: string
    purpose: string | null
    subject_template: string | null
    body_template: string | null
    variables: string[]
    message_count: number
}

interface ClientCommsPanelProps {
    omnibusClaimId: number
}

const CATEGORY_LABELS: Record<string, string> = {
    onboarding: 'Onboarding',
    claim_update: 'Claim update',
    open_banking_chase: 'Open Banking chase',
    lead_chase: 'Chase',
    no_finance_found: 'No finance found',
    more_claims_search: 'More claims search',
    feedback_request: 'Feedback request',
    cancellation: 'Cancellation',
    fraud_warning: 'Fraud warning',
    admin_confirmation: 'Admin confirmation',
    inbound: 'Inbound',
    call: 'Call',
    other: 'Other',
}

const formatDateTime = (value: string | null) => {
    if (!value) return 'Date unknown'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date)
}

const channelIcon = (channel: string) => {
    if (channel === 'sms') return <FaCommentDots />
    if (channel === 'call') return <FaPhone />
    return <FaEnvelope />
}

export const ClientCommsPanel = ({ omnibusClaimId }: ClientCommsPanelProps) => {
    const isAdmin = useIsAdmin()
    const [messages, setMessages] = useState<ClientCommMessage[]>([])
    const [loading, setLoading] = useState(false)
    const [openEmailId, setOpenEmailId] = useState<number | null>(null)
    const [emailHtml, setEmailHtml] = useState<Record<number, string>>({})
    const [showBackfill, setShowBackfill] = useState(false)
    const [templates, setTemplates] = useState<CommTemplate[]>([])
    const [backfillTemplateId, setBackfillTemplateId] = useState<number | ''>('')
    const [backfillSentAt, setBackfillSentAt] = useState('')
    const [backfillPreview, setBackfillPreview] = useState<{ subject: string | null; html: string | null; text: string | null } | null>(null)
    const [backfillBusy, setBackfillBusy] = useState(false)

    const loadMessages = useCallback(async () => {
        setLoading(true)
        try {
            const response = await api.post('omnibus-client-comms-list', { omnibus_claim_id: omnibusClaimId })
            if (response.data?.success) setMessages(response.data.messages || [])
        } catch (err: any) {
            console.error('Failed to load client comms', err)
        } finally {
            setLoading(false)
        }
    }, [omnibusClaimId])

    useEffect(() => {
        setMessages([])
        setOpenEmailId(null)
        setEmailHtml({})
        setShowBackfill(false)
        setBackfillPreview(null)
        loadMessages()
    }, [loadMessages])

    const toggleEmail = async (message: ClientCommMessage) => {
        if (openEmailId === message.id) {
            setOpenEmailId(null)
            return
        }
        setOpenEmailId(message.id)
        if (emailHtml[message.id] !== undefined) return
        try {
            const response = await api.post(
                'omnibus-client-comms-html',
                { message_id: message.id, omnibus_claim_id: omnibusClaimId },
                { responseType: 'text', transformResponse: [(data) => data] },
            )
            setEmailHtml((current) => ({ ...current, [message.id]: String(response.data ?? '') }))
        } catch (err: any) {
            setEmailHtml((current) => ({ ...current, [message.id]: '' }))
            Report.failure('Error', 'Failed to load the stored email for this message.', 'Okay')
        }
    }

    const openBackfill = async () => {
        setShowBackfill(true)
        setBackfillPreview(null)
        if (templates.length) return
        try {
            const response = await api.post('omnibus-client-comms-templates', {})
            if (response.data?.success) setTemplates(response.data.templates || [])
        } catch (err: any) {
            Report.failure('Error', 'Failed to load communication templates.', 'Okay')
        }
    }

    const previewBackfill = async () => {
        if (!backfillTemplateId) return
        setBackfillBusy(true)
        try {
            const response = await api.post('omnibus-client-comms-backfill', {
                omnibus_claim_id: omnibusClaimId,
                template_id: backfillTemplateId,
                preview: true,
            })
            if (response.data?.success) {
                setBackfillPreview({
                    subject: response.data.subject || null,
                    html: response.data.body_html || null,
                    text: response.data.body_text || null,
                })
            }
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || 'Failed to render the template preview.', 'Okay')
        } finally {
            setBackfillBusy(false)
        }
    }

    const confirmBackfill = async () => {
        if (!backfillTemplateId) return
        setBackfillBusy(true)
        try {
            const response = await api.post('omnibus-client-comms-backfill', {
                omnibus_claim_id: omnibusClaimId,
                template_id: backfillTemplateId,
                sent_at: backfillSentAt || undefined,
            })
            if (response.data?.success) {
                setShowBackfill(false)
                setBackfillPreview(null)
                setBackfillTemplateId('')
                setBackfillSentAt('')
                await loadMessages()
            } else {
                Report.failure('Error', response.data?.message || 'Backfill failed.', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Error', err?.response?.data?.message || 'Backfill failed.', 'Okay')
        } finally {
            setBackfillBusy(false)
        }
    }

    const counts = useMemo(() => {
        const emails = messages.filter((m) => m.channel === 'email').length
        const sms = messages.filter((m) => m.channel === 'sms').length
        return { emails, sms }
    }, [messages])

    return (
        <section className={styles.panel}>
            <div className={styles.panelHeader}>
                <h4 className={styles.panelTitle}>
                    Communications history
                    <span className={styles.panelCount}>
                        {messages.length ? `${counts.emails} emails · ${counts.sms} SMS` : '—'}
                    </span>
                </h4>
                <div className={styles.panelActions}>
                    <button type="button" className={styles.iconButton} onClick={loadMessages} title="Reload">
                        <FaSyncAlt />
                    </button>
                    {isAdmin && (
                        <button type="button" className={styles.backfillButton} onClick={openBackfill}>
                            <FaPlus /> Record missing comm
                        </button>
                    )}
                </div>
            </div>

            {isAdmin && showBackfill && (
                <div className={styles.backfillForm}>
                    <p className={styles.backfillHint}>
                        Record a message that was sent to this client but never logged. Pick the
                        template that was used and, if known, when it went out.
                    </p>
                    <div className={styles.backfillRow}>
                        <select
                            value={backfillTemplateId}
                            onChange={(e) => {
                                setBackfillTemplateId(e.target.value ? Number(e.target.value) : '')
                                setBackfillPreview(null)
                            }}
                        >
                            <option value="">Select a template…</option>
                            {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                    [{CATEGORY_LABELS[template.category] || template.category} · {template.channel}]{' '}
                                    {(template.subject_template || template.body_template || template.template_key).slice(0, 80)}
                                </option>
                            ))}
                        </select>
                        <input
                            type="datetime-local"
                            value={backfillSentAt}
                            onChange={(e) => setBackfillSentAt(e.target.value)}
                            title="Approximate sent date/time (optional)"
                        />
                        <button type="button" disabled={!backfillTemplateId || backfillBusy} onClick={previewBackfill}>
                            Preview
                        </button>
                        <button
                            type="button"
                            className={styles.confirmButton}
                            disabled={!backfillTemplateId || backfillBusy || !backfillPreview}
                            onClick={confirmBackfill}
                        >
                            Record against client
                        </button>
                        <button type="button" onClick={() => setShowBackfill(false)}>Cancel</button>
                    </div>
                    {backfillPreview && (
                        <div className={styles.backfillPreview}>
                            {backfillPreview.subject && <div className={styles.previewSubject}>{backfillPreview.subject}</div>}
                            {backfillPreview.html ? (
                                <iframe
                                    title="Template preview"
                                    sandbox=""
                                    srcDoc={backfillPreview.html}
                                    className={styles.emailFrame}
                                />
                            ) : (
                                <div className={styles.smsBody}>{backfillPreview.text}</div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {loading && <div className={styles.emptyState}>Loading communications…</div>}
            {!loading && messages.length === 0 && (
                <div className={styles.emptyState}>No recorded communications for this client.</div>
            )}

            {!loading && messages.length > 0 && (
                <ol className={styles.timeline}>
                    {messages.map((message) => (
                        <li key={message.id} className={styles.timelineItem} data-direction={message.direction}>
                            <div
                                className={`${styles.messageRow} ${message.channel === 'email' && message.has_html ? styles.clickable : ''}`}
                                onClick={() => {
                                    if (message.channel === 'email' && message.has_html) toggleEmail(message)
                                }}
                                role={message.channel === 'email' && message.has_html ? 'button' : undefined}
                            >
                                <span className={styles.channelBadge} data-channel={message.channel}>
                                    {channelIcon(message.channel)} {message.channel.toUpperCase()}
                                </span>
                                <span className={styles.messageMain}>
                                    <span className={styles.messageTitle}>
                                        {message.channel === 'email'
                                            ? (message.subject || '(no subject)')
                                            : (message.body_text || '(no content)')}
                                    </span>
                                    <span className={styles.messageMeta}>
                                        {formatDateTime(message.sent_at)}
                                        {message.direction === 'inbound' && <em className={styles.inboundTag}>inbound</em>}
                                        <em className={styles.categoryTag} data-category={message.category}>
                                            {CATEGORY_LABELS[message.category] || message.category}
                                        </em>
                                    </span>
                                </span>
                            </div>
                            {openEmailId === message.id && (
                                emailHtml[message.id] === undefined ? (
                                    <div className={styles.emptyState}>Loading email…</div>
                                ) : emailHtml[message.id] ? (
                                    <iframe
                                        title={message.subject || `Email ${message.id}`}
                                        sandbox=""
                                        srcDoc={emailHtml[message.id]}
                                        className={styles.emailFrame}
                                    />
                                ) : (
                                    <div className={styles.emptyState}>The stored email could not be loaded.</div>
                                )
                            )}
                        </li>
                    ))}
                </ol>
            )}
        </section>
    )
}
